import { memo, useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Typography,
    Box
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

const isHomogeneousArray = (arr: any[]): boolean => {
    if (!Array.isArray(arr) || arr.length === 0) return false
    if (!arr.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) return false
    const keys = Object.keys(arr[0])
    return arr.every(item => {
        const itemKeys = Object.keys(item)
        return keys.length === itemKeys.length && keys.every(k => itemKeys.includes(k))
    })
}

const CollapsibleRow = memo(({ label, value }: { label: string, value: any }) => {
    const [open, setOpen] = useState(false)
    const isExpandable = typeof value === 'object' && value !== null

    return (
        <>
            <TableRow>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isExpandable && (
                            <IconButton size='small' onClick={() => setOpen(!open)}>
                                {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        )}
                        <Typography>{label}</Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    {!isExpandable ? String(value) : null}
                </TableCell>
            </TableRow>
            {isExpandable && (
                <TableRow>
                    <TableCell colSpan={2} sx={{ p: 0, border: 0 }}>
                        <Collapse in={open} timeout='auto' unmountOnExit>
                            <Box sx={{ padding: 2 }}>
                                <RecursiveTable data={value} />
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
})

CollapsibleRow.displayName = 'CollapsibleRow'

const HomogeneousArrayTable = memo(({ data }: { data: any[] }) => {
    if (data.length === 0) return <span>Empty array</span>
    const columns = Object.keys(data[0])
    return (
        <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        {columns.map(col => (
                            <TableCell key={col}>
                                {col}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((row, idx) => (
                        <TableRow key={idx}>
                            {columns.map(col => (
                                <TableCell key={col}>
                                    {Array.isArray(row[col]) && isHomogeneousArray(row[col])
                                        ? <HomogeneousArrayTable data={row[col]} />
                                        : typeof row[col] === 'object' && row[col] !== null
                                            ? <RecursiveTable data={row[col]} />
                                            : String(row[col])}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
})

HomogeneousArrayTable.displayName = 'HomogeneousArrayTable'

const RecursiveTable = ({ data }: { data: any }) => {
    if (typeof data !== 'object' || data === null) return <span>{String(data)}</span>

    if (Array.isArray(data)) {
        if (isHomogeneousArray(data)) return <HomogeneousArrayTable data={data} />
        return (
            <TableContainer component={Paper} sx={{ width: '100%' }}>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>Index</strong>
                            </TableCell>
                            <TableCell>
                                <strong>Value</strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((item, idx) => (
                            <CollapsibleRow key={idx} label={String(idx)} value={item} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    return (
        <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table size='small' stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <strong>Key</strong>
                        </TableCell>
                        <TableCell>
                            <strong>Value</strong>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(data).map(([key, value]) => (
                        <CollapsibleRow key={key} label={key} value={value} />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default memo(RecursiveTable)
